import React, { useState, useContext } from 'react';
import { Sun, Droplets, ChevronDown, ChevronUp, Shield, Palette, Sparkles, Container, Leaf, Activity, Sprout } from 'lucide-react';
import {
  GROWTH_TYPE_LABELS,
  GROWTH_SPEED_LABELS,
  CARE_PRIORITY_LABELS,
  SOIL_TYPE_LABELS,
  TOXICITY_LEVEL_LABELS,
  TOXICITY_LEVEL_COLORS,
  SCENT_INTENSITY_LABELS,
  ROOT_TYPE_LABELS,
  ROOT_TYPE_TOOLTIPS
} from '../../../utils/plantFilters';
import PhSlider from './PhSlider';
import FloweringMonthsSlider from './FloweringMonthsSlider';
import MaxHeightSlider from './MaxHeightSlider';

/**
 * PlantCatalogSidebar - Collapsible filter sidebar for plant catalog
 *
 * Features:
 * - 300px fixed width sidebar
 * - Collapsible filter sections
 * - Multi-select filter chips
 * - Icon-labeled sections
 * - Reset button at bottom (only visible when filters active)
 *
 * Filter Sections:
 * 1. Sonnenlicht (sunlight)
 * 2. Wasserbedarf (water_requirement)
 * 3. Giftigkeit (toxicity - toxic_to_children, toxic_to_pets, non_toxic)
 * 4. Blütenfarbe (flower_color)
 * 5. Eigenschaften (evergreen, bee_friendly, frost_hardy, drought_tolerant)
 * 6. Pflanzgefäß (suitable_for_pot, suitable_for_planter)
 *
 * @param {Object} props - Component props
 * @param {Object} props.filters - Active filters object
 * @param {Function} props.onFilterChange - Callback for filter changes
 * @param {Function} props.onResetFilters - Callback to reset all filters
 */
/**
 * Context that supplies the sidebar's filter state and handlers to the
 * module-level FilterChip/FilterSection components. Hoisting these components out
 * of the render keeps their identity stable across renders (react-hooks/
 * static-components); the per-render state/handlers flow through this context so
 * the ~40 call sites stay unchanged.
 */
const PlantCatalogSidebarContext = React.createContext(null);

// Filter chip button component (module-level; state/handlers via context)
const FilterChip = ({ category, value, label, title }) => {
  const { isFilterActive, handleFilterToggle } = useContext(PlantCatalogSidebarContext);
  const active = isFilterActive(category, value);
  return (
    <button
      onClick={() => handleFilterToggle(category, value)}
      className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-colors duration-200 whitespace-nowrap ${
        active
          ? 'bg-emerald-100 border-emerald-300 text-emerald-700'
          : 'bg-white border-gray-300 text-gray-700 hover:border-emerald-400'
      }`}
      title={title} // Tooltip on hover
    >
      {label}
    </button>
  );
};

// Collapsible section component (module-level; state/handlers via context).
// `expanded` is derived from context by `id`, so call sites may still pass an
// (ignored) expanded prop without effect.
const FilterSection = ({ id, icon: Icon, iconColor, title, children }) => {
  const { toggleSection, expandedSections } = useContext(PlantCatalogSidebarContext);
  const expanded = expandedSections[id];
  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        onClick={() => toggleSection(id)}
        className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors duration-200"
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${iconColor}`} />
          <span className="text-sm font-medium text-gray-700">{title}</span>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        )}
      </button>
      {expanded && (
        <div className="px-3 pb-3">
          <div className="flex flex-wrap gap-2">
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

const PlantCatalogSidebar = ({ filters, onFilterChange, onResetFilters, gardenFilterActive = false }) => {
  // Track which sections are expanded (all collapsed by default)
  const [expandedSections, setExpandedSections] = useState({
    sunlight: false,
    water: false,
    safety: false,     // Sicherheit (extended with cat/dog + toxicity level)
    flowers: false,    // RENAMED from "color" - now "Blüten und Duft"
    growth: false,     // Wuchs
    care: false,       // Pflege
    soil: false,       // Boden (extended with Bodentyp)
    properties: false, // Eigenschaften (extended with Wurzeltyp + Winterschutz)
    container: false,  // Pflanzgefäß
  });

  // Auto-expand relevant sections when the garden filter is activated. Adjusted
  // during render (previous-value tracking) rather than in an effect to avoid a
  // synchronous setState-in-effect cascade. Seeded false so an already-active
  // gardenFilterActive on mount still expands (matching the old mount effect).
  const [prevGardenFilterActive, setPrevGardenFilterActive] = useState(false);
  if (gardenFilterActive !== prevGardenFilterActive) {
    setPrevGardenFilterActive(gardenFilterActive);
    if (gardenFilterActive) {
      setExpandedSections(prev => ({
        ...prev,
        sunlight: true,
        water: true,
        soil: true,
      }));
    }
  }


  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Toggle filter chip
  const handleFilterToggle = (filterCategory, value) => {
    if (!onFilterChange) return;

    const currentValues = filters[filterCategory] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];

    onFilterChange(filterCategory, newValues);
  };

  // Check if filter is active
  const isFilterActive = (filterCategory, value) => {
    const currentValues = filters[filterCategory] || [];
    return currentValues.includes(value);
  };

  // Check if any filters are active (including pH filter and flowering months)
  const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
    if (key === 'ph_value') {
      // pH filter active if set and not default (7.0)
      return value !== null && value !== 7.0;
    }
    if (key === 'flowering_months') {
      // Flowering months active if set and not default (1-12)
      return value !== null && (value.start !== 1 || value.end !== 12);
    }
    if (key === 'max_height_cm') {
      // Max-height filter active if an upper bound is set (null = no filter)
      return value !== null;
    }
    return Array.isArray(value) && value.length > 0;
  });

  /**
   * Handle pH slider change - delegated to PhSlider component
   */
  const handlePhChange = React.useCallback((value) => {
    if (onFilterChange) {
      onFilterChange('ph_value', value);
    }
  }, [onFilterChange]);

  /**
   * Handle flowering months slider change - delegated to FloweringMonthsSlider component
   */
  const handleFloweringMonthsChange = React.useCallback((value) => {
    if (onFilterChange) {
      onFilterChange('flowering_months', value);
    }
  }, [onFilterChange]);

  /**
   * Handle max-height slider change - delegated to MaxHeightSlider component
   */
  const handleMaxHeightChange = React.useCallback((value) => {
    if (onFilterChange) {
      onFilterChange('max_height_cm', value);
    }
  }, [onFilterChange]);

  const sidebarContextValue = {
    isFilterActive,
    handleFilterToggle,
    toggleSection,
    expandedSections,
  };

  return (
    <PlantCatalogSidebarContext.Provider value={sidebarContextValue}>
    <div className="w-[300px] bg-white border-r border-gray-200 flex flex-col">
      {/* Sidebar Header */}
      <div className="p-3 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700">Filter</h3>
      </div>

      {/* Filter Sections - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        <div className="flex flex-col">
          {/* 1. Sonnenlicht */}
          <FilterSection
            id="sunlight"
            icon={Sun}
            iconColor="text-amber-500"
            title="Sonnenlicht"
            expanded={expandedSections.sunlight}
          >
            <FilterChip category="sunlight" value="full_sun" label="Volle Sonne" />
            <FilterChip category="sunlight" value="sun-partial_shade" label="Sonne-Halbschatten" />
            <FilterChip category="sunlight" value="partial_shade" label="Halbschatten" />
            <FilterChip category="sunlight" value="full_shade" label="Schatten" />
          </FilterSection>

          {/* 2. Wasserbedarf */}
          <FilterSection
            id="water"
            icon={Droplets}
            iconColor="text-blue-500"
            title="Wasserbedarf"
            expanded={expandedSections.water}
          >
            <FilterChip category="water_requirement" value="low" label="Niedrig" />
            <FilterChip category="water_requirement" value="medium" label="Mittel" />
            <FilterChip category="water_requirement" value="high" label="Hoch" />
          </FilterSection>

          {/* 3. Sicherheit - EXTENDED with cat/dog filters + toxicity level */}
          <FilterSection
            id="safety"
            icon={Shield}
            iconColor="text-red-500"
            title="Sicherheit"
            expanded={expandedSections.safety}
          >
            {/* Basic safety filters */}
            <FilterChip category="toxic" value="non_toxic" label="Ungiftig" />
            <FilterChip category="toxic" value="safe_for_children" label="Ungiftig für Kinder" />
            <FilterChip category="toxic" value="safe_for_pets" label="Ungiftig für Haustiere" />
            {/* NEW: Pet-specific filters */}
            <FilterChip category="safe_for_dogs" value="true" label="Ungiftig für Hunde" />
            <FilterChip category="safe_for_cats" value="true" label="Ungiftig für Katzen" />
            <FilterChip category="has_thorns" value="true" label="Ohne Dornen/Stacheln" />

            {/* Divider */}
            <div className="w-full border-t border-gray-200 my-2"></div>

            {/* NEW: Detailed Toxicity Level */}
            <div className="w-full">
              <span className="text-xs font-medium text-gray-600 mb-1.5 block">Giftigkeitsstufe</span>
              <div className="flex flex-wrap gap-2">
                {Object.entries(TOXICITY_LEVEL_LABELS).map(([value, label]) => {
                  const color = TOXICITY_LEVEL_COLORS[value];
                  const isSevere = value === 'severe';

                  return (
                    <button
                      key={value}
                      onClick={() => handleFilterToggle('toxicity_level', value)}
                      className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-colors duration-200 whitespace-nowrap ${
                        isFilterActive('toxicity_level', value)
                          ? 'bg-emerald-100 border-emerald-300 text-emerald-700'
                          : 'bg-white border-gray-300 text-gray-700 hover:border-emerald-400'
                      }`}
                      style={{
                        // Color-coded left border for visual distinction
                        borderLeft: `4px solid ${color}`
                      }}
                    >
                      {isSevere && '⚠️ '}{label}
                    </button>
                  );
                })}
              </div>
            </div>
          </FilterSection>

          {/* 4. Blüten und Duft - RENAMED from "Blütenfarbe" & EXTENDED */}
          <FilterSection
            id="flowers"
            icon={Palette}
            iconColor="text-pink-500"
            title="Blüten und Duft"
            expanded={expandedSections.flowers}
          >
            {/* Blütenfarbe - EXISTING */}
            <div className="w-full">
              <span className="text-xs font-medium text-gray-600 mb-1.5 block">Blütenfarbe</span>
              <div className="flex flex-wrap gap-2">
                <FilterChip category="flower_color" value="white" label="Weiß" />
                <FilterChip category="flower_color" value="yellow" label="Gelb" />
                <FilterChip category="flower_color" value="orange" label="Orange" />
                <FilterChip category="flower_color" value="red" label="Rot" />
                <FilterChip category="flower_color" value="pink" label="Rosa" />
                <FilterChip category="flower_color" value="purple" label="Lila" />
                <FilterChip category="flower_color" value="violet" label="Violett" />
                <FilterChip category="flower_color" value="blue" label="Blau" />
                <FilterChip category="flower_color" value="green" label="Grün" />
              </div>
            </div>

            {/* Divider */}
            <div className="w-full border-t border-gray-200 my-2"></div>

            {/* NEW: Duftintensität */}
            <div className="w-full">
              <span className="text-xs font-medium text-gray-600 mb-1.5 block">Duftintensität</span>
              <div className="flex flex-wrap gap-2">
                {Object.entries(SCENT_INTENSITY_LABELS).map(([value, label]) => (
                  <FilterChip
                    key={value}
                    category="scent_intensity"
                    value={value}
                    label={label}
                  />
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="w-full border-t border-gray-200 my-2"></div>

            {/* NEW: Blütezeit Slider */}
            <FloweringMonthsSlider
              value={filters.flowering_months}
              onChange={handleFloweringMonthsChange}
            />
          </FilterSection>

          {/* 5. Wuchs - NEW SECTION */}
          <FilterSection
            id="growth"
            icon={Leaf}
            iconColor="text-green-600"
            title="Wuchs"
            expanded={expandedSections.growth}
          >
            {/* Wuchsform - 9 chips in rows */}
            <div className="w-full">
              <span className="text-xs font-medium text-gray-600 mb-1.5 block">Wuchsform</span>
              <div className="flex flex-wrap gap-2">
                {Object.entries(GROWTH_TYPE_LABELS).map(([value, label]) => (
                  <FilterChip
                    key={value}
                    category="care_growth_type"
                    value={value}
                    label={label}
                  />
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="w-full border-t border-gray-200 my-2"></div>

            {/* Wachstumsgeschwindigkeit - 3 chips */}
            <div className="w-full">
              <span className="text-xs font-medium text-gray-600 mb-1.5 block">Wachstum</span>
              <div className="flex flex-wrap gap-2">
                {Object.entries(GROWTH_SPEED_LABELS).map(([value, label]) => (
                  <FilterChip
                    key={value}
                    category="growth_speed"
                    value={value}
                    label={label}
                  />
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="w-full border-t border-gray-200 my-2"></div>

            {/* Kletterpflanzen - Checkbox */}
            <FilterChip category="is_climber" value="true" label="Nur Kletterpflanzen" />

            {/* Divider */}
            <div className="w-full border-t border-gray-200 my-2"></div>

            {/* Maximale Wuchshöhe Slider (#762) */}
            <MaxHeightSlider
              value={filters.max_height_cm}
              onChange={handleMaxHeightChange}
            />
          </FilterSection>

          {/* 6. Pflege - NEW SECTION */}
          <FilterSection
            id="care"
            icon={Activity}
            iconColor="text-purple-600"
            title="Pflege"
            expanded={expandedSections.care}
          >
            <div className="w-full">
              <span className="text-xs font-medium text-gray-600 mb-1.5 block">Pflegeaufwand</span>
              <div className="flex flex-wrap gap-2">
                {Object.entries(CARE_PRIORITY_LABELS).map(([value, label]) => (
                  <FilterChip
                    key={value}
                    category="care_priority"
                    value={value}
                    label={label}
                  />
                ))}
              </div>
            </div>
          </FilterSection>

          {/* 7. Boden - EXTENDED with Bodentyp */}
          <FilterSection
            id="soil"
            icon={Sprout}
            iconColor="text-amber-600"
            title="Boden"
            expanded={expandedSections.soil}
          >
            {/* pH-Wert Slider (EXISTING) */}
            <PhSlider
              value={filters.ph_value}
              onChange={handlePhChange}
            />

            {/* Divider */}
            <div className="w-full border-t border-gray-200 my-2"></div>

            {/* NEW: Bodentyp */}
            <div className="w-full">
              <span className="text-xs font-medium text-gray-600 mb-1.5 block">Bodentyp</span>
              <div className="flex flex-wrap gap-2">
                {Object.entries(SOIL_TYPE_LABELS).map(([value, label]) => (
                  <FilterChip
                    key={value}
                    category="soil_type"
                    value={value}
                    label={label}
                  />
                ))}
              </div>
            </div>
          </FilterSection>

          {/* 8. Eigenschaften - EXTENDED */}
          <FilterSection
            id="properties"
            icon={Sparkles}
            iconColor="text-emerald-500"
            title="Eigenschaften"
            expanded={expandedSections.properties}
          >
            {/* Existing filters */}
            <FilterChip category="evergreen" value="true" label="Immergrün" />
            <FilterChip category="bee_friendly" value="high" label="Bienenfreundlich" />
            <FilterChip category="frost_hardy" value="true" label="Frosthart" />
            <FilterChip category="drought_tolerant" value="true" label="Trockenheitsverträglich" />

            {/* Divider */}
            <div className="w-full border-t border-gray-200 my-2"></div>

            {/* NEW: Wurzeltyp */}
            <div className="w-full">
              <span className="text-xs font-medium text-gray-600 mb-1.5 block">Wurzeltyp</span>
              <div className="flex flex-wrap gap-2">
                {Object.entries(ROOT_TYPE_LABELS).map(([value, label]) => (
                  <FilterChip
                    key={value}
                    category="root_type"
                    value={value}
                    label={label}
                    title={ROOT_TYPE_TOOLTIPS[value]}
                  />
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="w-full border-t border-gray-200 my-2"></div>

            {/* NEW: Winterschutz */}
            <FilterChip category="winter_protection" value="true" label="Ohne Winterschutz" />
          </FilterSection>

          {/* 9. Pflanzgefäß */}
          <FilterSection
            id="container"
            icon={Container}
            iconColor="text-purple-500"
            title="Pflanzgefäß"
            expanded={expandedSections.container}
          >
            <FilterChip category="suitable_for_pot" value="true" label="Kübel geeignet" />
            <FilterChip category="suitable_for_planter" value="true" label="Balkonkasten" />
          </FilterSection>
        </div>
      </div>

      {/* Reset Button - Fixed at bottom, only visible when filters active */}
      {hasActiveFilters && (
        <div className="p-3 border-t border-gray-200">
          <button
            onClick={onResetFilters}
            className="w-full px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium rounded transition-colors duration-200"
            aria-label="Alle Filter zurücksetzen"
          >
            Filter zurücksetzen
          </button>
        </div>
      )}
    </div>
    </PlantCatalogSidebarContext.Provider>
  );
};

export default PlantCatalogSidebar;
